import { includes } from 'lodash';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { EntityKind } from '@/types/schema';

import {
  BUSINESS_ENTITY_TYPES,
  NON_TRUST_CHARITABLE_ENTITY_TYPES,
  PERSONAL_FAMILY_ACCOUNT_TYPES,
  TRUST_ENTITY_TYPES,
} from '../entities.constants';
import { getEntityTypeFromEntityKind } from './getEntityTypeFromEntityKind';

export interface EntityForGrouping {
  id: string;
  kind: EntityKind;
  subtype: {
    displayName: string;
  };
  disabled?: boolean;
}

export interface GroupedEntityOptions {
  directHoldings: TypeaheadSelectInputOption<string>[];
  trusts: TypeaheadSelectInputOption<string>[];
  businesses: TypeaheadSelectInputOption<string>[];
  other: TypeaheadSelectInputOption<string>[];
}

/**
 * Groups entities into categories for consistent UI display across the application.
 * Used by both liability recipients and estate holdings forms.
 *
 * @param entities - Array of entities to group
 * @param currentEntityId - Optional ID of the current entity to show as "This entity"
 * @returns Object with arrays of options for each category
 */
export function groupEntitiesByCategory(
  entities: EntityForGrouping[],
  currentEntityId?: string
): GroupedEntityOptions {
  const directHoldings: TypeaheadSelectInputOption<string>[] = [];
  const trusts: TypeaheadSelectInputOption<string>[] = [];
  const businesses: TypeaheadSelectInputOption<string>[] = [];
  const other: TypeaheadSelectInputOption<string>[] = [];

  entities.forEach((entity) => {
    const entityType = getEntityTypeFromEntityKind(entity.kind);
    const option: TypeaheadSelectInputOption<string> = {
      value: entity.id,
      display:
        entity.id === currentEntityId
          ? `This entity (${entity.subtype.displayName})`
          : entity.subtype.displayName,
      ...(entity.disabled !== undefined && { disabled: entity.disabled }),
    };

    if (includes(PERSONAL_FAMILY_ACCOUNT_TYPES, entityType)) {
      directHoldings.push(option);
    } else if (
      includes(TRUST_ENTITY_TYPES, entityType) ||
      includes(NON_TRUST_CHARITABLE_ENTITY_TYPES, entityType)
    ) {
      trusts.push(option);
    } else if (includes(BUSINESS_ENTITY_TYPES, entityType)) {
      businesses.push(option);
    } else {
      // Fallback for any entity types not covered by the above categories
      other.push(option);
    }
  });

  // Sort each category alphabetically, but keep "This entity" at the top within its category
  const sortOptions = (options: TypeaheadSelectInputOption<string>[]) =>
    options.sort((a, b) => {
      if (a.value === currentEntityId) return -1;
      if (b.value === currentEntityId) return 1;
      return a.display.localeCompare(b.display);
    });

  return {
    directHoldings: sortOptions(directHoldings),
    trusts: sortOptions(trusts),
    businesses: sortOptions(businesses),
    other: sortOptions(other),
  };
}

/**
 * Creates a flattened array of options with group labels for use in TypeaheadSelectInput.
 * This version is compatible with TypeaheadSelectInputOption structure using groupName.
 *
 * @param grouped - Result from groupEntitiesByCategory
 * @returns Flattened array with groupName set for each option
 */
export function createGroupedOptionsForTypeaheadSelect(
  grouped: GroupedEntityOptions
): TypeaheadSelectInputOption<string>[] {
  const options: TypeaheadSelectInputOption<string>[] = [];

  if (grouped.directHoldings.length > 0) {
    grouped.directHoldings.forEach((option) => {
      options.push({
        ...option,
        groupName: 'Direct holdings',
      });
    });
  }

  if (grouped.trusts.length > 0) {
    grouped.trusts.forEach((option) => {
      options.push({
        ...option,
        groupName: 'Trusts',
      });
    });
  }

  if (grouped.businesses.length > 0) {
    grouped.businesses.forEach((option) => {
      options.push({
        ...option,
        groupName: 'Businesses',
      });
    });
  }

  if (grouped.other.length > 0) {
    grouped.other.forEach((option) => {
      options.push({
        ...option,
        groupName: 'Other',
      });
    });
  }

  return options;
}
